import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { KPI, TKpiSliceState, TPhaseCountsOfGeneralSeaAreas, TPhasePercentages, TSignalCountsOnSpeedRanges, TKeyMetrics, TVesselSpeedDraughtValues, TSectorKeyMetrics, TSectorPhaseCountsOfGeneralSeaAreas, TSectorPhasePercentages, TSectorSignalCountsOnSpeedRanges, TPolygonCalls, TVoyages, TStoppageEvents, TVesselVoyagesHourlyTracks, TActivityCoverage } from './kpiTypes';

const initialState: TKpiSliceState = {
    summaryPhasePercentages: {
        sectorListPhasePercentages: null,
        loading: false,
        loadingError: null,
    },
    summaryPhaseCountsOfGeneralSeaAreas: {
        sectorListPhaseCountsOfGeneralSeaAreas: null,
        loading: false,
        loadingError: null,
    },
    summarySignalCountsOnSpeedRanges: {
        sectorListSignalCountsOnSpeedRanges: null,
        loading: false,
        loadingError: null,
    },
    summaryKeyMetrics: {
        sectorListKeyMetrics: null,
        loading: false,
        loadingError: null,
    },

    vesselListPhasePercentages: [],
    vesselListPhaseCountsOfGeneralSeaAreas: [],
    vesselListSignalCountsOnSpeedRanges: [],
    vesselListKeyMetrics: [],
    vesselListSpeedDraughtValues: [],

    vesselListPolygonCalls: [],
    vesselListVoyages: [],
    vesselListStoppageEvents: [],
    vesselCohortVoyages: null,
    vesselCohortSignalCountsOnSpeedRanges: null,
    vesselCohortPhasePercentagesOfPolygonCalls: null,

    vesselVoyagesHourlyTracks: null,

    activityCoverage: null,
    sectorActivityCoverage: null,
};

const kpiSlice = createSlice({
    name: KPI,
    initialState,
    reducers: {
        getPhasePercentagesRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
            const vesselPhasePercentages: TPhasePercentages = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null,
                isFetched: false
            };

            if (!state.vesselListPhasePercentages) {
                state.vesselListPhasePercentages = [];
            }
            state.vesselListPhasePercentages.push(vesselPhasePercentages);
        },
        getPhasePercentagesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TPhasePercentages>,
        ) => {
            if (state.vesselListPhasePercentages) {
                state.vesselListPhasePercentages = (state.vesselListPhasePercentages || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getPhasePercentagesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<any>,
        ) => {
            if (state.vesselListPhasePercentages) {
                state.vesselListPhasePercentages = (state.vesselListPhasePercentages || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },
        getPhaseCountsOfGeneralSeaAreasRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
            const vesselPhaseCountsOfGeneralSeaAreas: TPhaseCountsOfGeneralSeaAreas = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            };

            if (!state.vesselListPhaseCountsOfGeneralSeaAreas) {
                state.vesselListPhaseCountsOfGeneralSeaAreas = [];
            }
            state.vesselListPhaseCountsOfGeneralSeaAreas.push(vesselPhaseCountsOfGeneralSeaAreas);
        },
        getPhaseCountsOfGeneralSeaAreasSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TPhaseCountsOfGeneralSeaAreas>,
        ) => {
            if (state.vesselListPhaseCountsOfGeneralSeaAreas) {
                state.vesselListPhaseCountsOfGeneralSeaAreas = (state.vesselListPhaseCountsOfGeneralSeaAreas || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getPhaseCountsOfGeneralSeaAreasErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TPhaseCountsOfGeneralSeaAreas>,
        ) => {
            if (state.vesselListPhaseCountsOfGeneralSeaAreas) {
                state.vesselListPhaseCountsOfGeneralSeaAreas = (state.vesselListPhaseCountsOfGeneralSeaAreas || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },
        getSignalCountsOnSpeedRangesRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
            const vesselSignalCountsOnSpeedRanges: TSignalCountsOnSpeedRanges = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            };

            if (!state.vesselListSignalCountsOnSpeedRanges) {
                state.vesselListSignalCountsOnSpeedRanges = [];
            }
            state.vesselListSignalCountsOnSpeedRanges.push(vesselSignalCountsOnSpeedRanges);
        },
        getSignalCountsOnSpeedRangesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TSignalCountsOnSpeedRanges>,
        ) => {
            if (state.vesselListSignalCountsOnSpeedRanges) {
                state.vesselListSignalCountsOnSpeedRanges = (state.vesselListSignalCountsOnSpeedRanges || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getSignalCountsOnSpeedRangesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TSignalCountsOnSpeedRanges>,
        ) => {
            if (state.vesselListSignalCountsOnSpeedRanges) {
                state.vesselListSignalCountsOnSpeedRanges = (state.vesselListSignalCountsOnSpeedRanges || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },
        getVesselKeyMetricsRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
            const vesselKeyMetrics: TKeyMetrics = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                data: null
            };

            if (!state.vesselListKeyMetrics) {
                state.vesselListKeyMetrics = [];
            }
            state.vesselListKeyMetrics.push(vesselKeyMetrics);
        },
        getVesselKeyMetricsSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TKeyMetrics>,
        ) => {
            if (state.vesselListKeyMetrics) {
                state.vesselListKeyMetrics = (state.vesselListKeyMetrics || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getVesselKeyMetricsErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TKeyMetrics>,
        ) => {
            if (state.vesselListKeyMetrics) {
                state.vesselListKeyMetrics = (state.vesselListKeyMetrics || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },
        getVesselSpeedAndDraughtOnLoadedConditionsRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) => {
            const vesselValues: TVesselSpeedDraughtValues = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            };

            if (!state.vesselListSpeedDraughtValues) {
                state.vesselListSpeedDraughtValues = [];
            }
            state.vesselListSpeedDraughtValues.push(vesselValues);
        },
        getVesselSpeedAndDraughtOnLoadedConditionsSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TVesselSpeedDraughtValues>,
        ) => {
            if (state.vesselListSpeedDraughtValues) {
                state.vesselListSpeedDraughtValues = (state.vesselListSpeedDraughtValues || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getVesselSpeedAndDraughtOnLoadedConditionsErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TVesselSpeedDraughtValues>,
        ) => {
            if (state.vesselListSpeedDraughtValues) {
                state.vesselListSpeedDraughtValues = (state.vesselListSpeedDraughtValues || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },

        getSummaryKeyMetricsRequestAction: (state: TKpiSliceState, _: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) => {

            if (state.summaryKeyMetrics) {
                state.summaryKeyMetrics.loadingError = null;
                state.summaryKeyMetrics.loading = true;
                state.summaryKeyMetrics.sectorListKeyMetrics = null;
            }
        },
        getSummaryKeyMetricsSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TSectorKeyMetrics[]>,
        ) => {
            if (state.summaryKeyMetrics) {
                state.summaryKeyMetrics.sectorListKeyMetrics = data;
                state.summaryKeyMetrics.loading = false;
                state.summaryKeyMetrics.loadingError = null;
            }
        },
        getSummaryKeyMetricsErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<any>,
        ) => {
            if (state.summaryKeyMetrics) {
                state.summaryKeyMetrics.sectorListKeyMetrics = null;
                state.summaryKeyMetrics.loading = false;
                state.summaryKeyMetrics.loadingError = {
                    message: (error.response && error.response.data) || error.message || '',
                    name: error.name,
                    stack: error.stack,
                };
            }
        },

        getSummaryPhaseCountsOfGeneralSeaAreasRequestAction: (state: TKpiSliceState, _: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) => {

            if (state.summaryPhaseCountsOfGeneralSeaAreas) {
                state.summaryPhaseCountsOfGeneralSeaAreas.loadingError = null;
                state.summaryPhaseCountsOfGeneralSeaAreas.loading = true;
                state.summaryPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas = null;
            }
        },
        getSummaryPhaseCountsOfGeneralSeaAreasSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TSectorPhaseCountsOfGeneralSeaAreas[]>,
        ) => {
            if (state.summaryPhaseCountsOfGeneralSeaAreas) {
                state.summaryPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas = data;
                state.summaryPhaseCountsOfGeneralSeaAreas.loading = false;
                state.summaryPhaseCountsOfGeneralSeaAreas.loadingError = null;
            }
        },
        getSummaryPhaseCountsOfGeneralSeaAreasErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<any>,
        ) => {
            if (state.summaryPhaseCountsOfGeneralSeaAreas) {
                state.summaryPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas = null;
                state.summaryPhaseCountsOfGeneralSeaAreas.loading = false;
                state.summaryPhaseCountsOfGeneralSeaAreas.loadingError = {
                    message: (error.response && error.response.data) || error.message || '',
                    name: error.name,
                    stack: error.stack,
                };
            }
        },
        getSummaryPhasePercentagesRequestAction: (state: TKpiSliceState, _: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) => {
            if (state.summaryPhasePercentages) {
                state.summaryPhasePercentages.loadingError = null;
                state.summaryPhasePercentages.loading = true;
                state.summaryPhasePercentages.sectorListPhasePercentages = null;
            }
        },
        getSummaryPhasePercentagesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TSectorPhasePercentages[]>,
        ) => {
            if (state.summaryPhasePercentages) {
                state.summaryPhasePercentages.sectorListPhasePercentages = data;
                state.summaryPhasePercentages.loading = false;
                state.summaryPhasePercentages.loadingError = null;
            }
        },
        getSummaryPhasePercentagesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<any>,
        ) => {
            if (state.summaryPhasePercentages) {
                state.summaryPhasePercentages.sectorListPhasePercentages = null;
                state.summaryPhasePercentages.loading = false;
                state.summaryPhasePercentages.loadingError = {
                    message: (error.response && error.response.data) || error.message || '',
                    name: error.name,
                    stack: error.stack,
                };
            }
        },
        getSummarySignalCountsOnSpeedRangesRequestAction: (state: TKpiSliceState, _: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) => {
            if (state.summarySignalCountsOnSpeedRanges) {
                state.summarySignalCountsOnSpeedRanges.loadingError = null;
                state.summarySignalCountsOnSpeedRanges.loading = true;
                state.summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges = null;
            }
        },
        getSummarySignalCountsOnSpeedRangesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TSectorSignalCountsOnSpeedRanges[]>,
        ) => {
            if (state.summarySignalCountsOnSpeedRanges) {
                state.summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges = data;
                state.summarySignalCountsOnSpeedRanges.loading = false;
                state.summarySignalCountsOnSpeedRanges.loadingError = null;
            }
        },
        getSummarySignalCountsOnSpeedRangesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<any>,
        ) => {
            if (state.summarySignalCountsOnSpeedRanges) {
                state.summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges = null;
                state.summarySignalCountsOnSpeedRanges.loading = false;
                state.summarySignalCountsOnSpeedRanges.loadingError = {
                    message: (error.response && error.response.data) || error.message || '',
                    name: error.name,
                    stack: error.stack,
                };
            }
        },
        getVesselPolygonCallsRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) => {
            const vesselPolygonCalls: TPolygonCalls = {
                imoNumber: action.payload!.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            };

            if (!state.vesselListPolygonCalls) {
                state.vesselListPolygonCalls = [];
            }
            state.vesselListPolygonCalls.push(vesselPolygonCalls);
        },
        getVesselPolygonCallsSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TPolygonCalls>,
        ) => {
            if (state.vesselListPolygonCalls) {
                state.vesselListPolygonCalls = (state.vesselListPolygonCalls || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getVesselPolygonCallsErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TPolygonCalls>,
        ) => {
            if (state.vesselListPolygonCalls) {
                state.vesselListPolygonCalls = (state.vesselListPolygonCalls || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },
        getVesselVoyagesRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) => {
            const vesselVoyages: TVoyages = {
                imoNumber: action.payload!.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null,
                isFetched: false
            };

            if (!state.vesselListVoyages) {
                state.vesselListVoyages = [];
            }
            state.vesselListVoyages.push(vesselVoyages);
        },
        getVesselVoyagesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TVoyages>,
        ) => {
            if (state.vesselListVoyages) {
                state.vesselListVoyages = (state.vesselListVoyages || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getVesselVoyagesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TVoyages>,
        ) => {
            if (state.vesselListVoyages) {
                state.vesselListVoyages = (state.vesselListVoyages || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },
        getVesselStoppageEventsRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) => {
            const vesselStoppageEvents: TStoppageEvents = {
                imoNumber: action.payload!.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            };

            if (!state.vesselListStoppageEvents) {
                state.vesselListStoppageEvents = [];
            }
            state.vesselListStoppageEvents.push(vesselStoppageEvents);
        },
        getVesselStoppageEventsSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TStoppageEvents>,
        ) => {
            if (state.vesselListStoppageEvents) {
                state.vesselListStoppageEvents = (state.vesselListStoppageEvents || []).map((p) =>
                    p.imoNumber === data.imoNumber ? data : p
                );
            }
        },
        getVesselStoppageEventsErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TStoppageEvents>,
        ) => {
            if (state.vesselListStoppageEvents) {
                state.vesselListStoppageEvents = (state.vesselListStoppageEvents || []).map((p) =>
                    p.imoNumber === error.imoNumber ? error : p
                );
            }
        },

        getVesselCohortVoyagesRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoList: number[], imoNumber: number, startDate: string, endDate: string }>) => {
            state.vesselCohortVoyages = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null,
                isFetched: false
            }
        },
        getVesselCohortVoyagesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TVoyages>,
        ) => {
            if (state.vesselCohortVoyages) {
                state.vesselCohortVoyages = data;
            }
        },
        getVesselCohortVoyagesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TVoyages>,
        ) => {
            if (state.vesselCohortVoyages) {
                state.vesselCohortVoyages = error;
            }
        },
        getVesselCohortSignalCountsOnSpeedRangesRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoList: number[], imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
            state.vesselCohortSignalCountsOnSpeedRanges = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            }
        },
        getVesselCohortSignalCountsOnSpeedRangesSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TSignalCountsOnSpeedRanges>,
        ) => {
            if (state.vesselCohortSignalCountsOnSpeedRanges) {
                state.vesselCohortSignalCountsOnSpeedRanges = data;
            }
        },
        getVesselCohortSignalCountsOnSpeedRangesErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TSignalCountsOnSpeedRanges>,
        ) => {
            if (state.vesselCohortSignalCountsOnSpeedRanges) {
                state.vesselCohortSignalCountsOnSpeedRanges = error;
            }
        },

        getVesselCohortPhasePercentagesOfPolygonCallsRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoList: number[], imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
            state.vesselCohortPhasePercentagesOfPolygonCalls = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null,
                isFetched: false
            }
        },
        getVesselCohortPhasePercentagesOfPolygonCallsSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TPhasePercentages>,
        ) => {
            if (state.vesselCohortPhasePercentagesOfPolygonCalls) {
                state.vesselCohortPhasePercentagesOfPolygonCalls = data;
            }
        },
        getVesselCohortPhasePercentagesOfPolygonCallsErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TPhasePercentages>,
        ) => {
            if (state.vesselCohortPhasePercentagesOfPolygonCalls) {
                state.vesselCohortPhasePercentagesOfPolygonCalls = error;
            }
        },

        getVesselVoyagesHourlyTracksRequestAction: (state: TKpiSliceState, action: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) => {
            state.vesselVoyagesHourlyTracks = {
                imoNumber: action.payload.imoNumber,
                loading: true,
                loadingError: null,
                dataList: null
            };
        },
        getVesselVoyagesHourlyTracksSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TVesselVoyagesHourlyTracks>,
        ) => {
            state.vesselVoyagesHourlyTracks = {
                imoNumber: data.imoNumber,
                loading: false,
                loadingError: null,
                dataList: data.dataList
            };
        },
        getVesselVoyagesHourlyTracksErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<TVesselVoyagesHourlyTracks>,
        ) => {
            state.vesselVoyagesHourlyTracks = {
                imoNumber: error.imoNumber,
                loading: false,
                loadingError: error.loadingError,
                dataList: null
            };
        },

        // Additional actions for activity coverage can be added here
        getActivityCoverageRequestAction: (state: TKpiSliceState, _: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number, 
            startDate: string, endDate: string }>) => {
            state.activityCoverage = {
                activityCoverageData: null,
                loading: true,
                loadingError: null
            };
        },
        getActivityCoverageSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TActivityCoverage>,
        ) => {
            state.activityCoverage = {
                activityCoverageData: data,
                loading: false,
                loadingError: null
            };
        },
        getActivityCoverageErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<Error>,
        ) => {
            state.activityCoverage = {
                activityCoverageData: null,
                loading: false,
                loadingError: error
            };
        },

        // Sector (Summary output) scoped activity coverage - keeps only the currently active sector's
        // result; every new sector selection overwrites this state with its own request/result.
        getSectorActivityCoverageRequestAction: (state: TKpiSliceState, _: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number,
            startDate: string, endDate: string }>) => {
            state.sectorActivityCoverage = {
                activityCoverageData: null,
                loading: true,
                loadingError: null
            };
        },
        getSectorActivityCoverageSuccessAction: (
            state: TKpiSliceState,
            { payload: data }: PayloadAction<TActivityCoverage>,
        ) => {
            state.sectorActivityCoverage = {
                activityCoverageData: data,
                loading: false,
                loadingError: null
            };
        },
        getSectorActivityCoverageErrorAction: (
            state: TKpiSliceState,
            { payload: error }: PayloadAction<Error>,
        ) => {
            state.sectorActivityCoverage = {
                activityCoverageData: null,
                loading: false,
                loadingError: error
            };
        }

    },
});

export const kpisActions = {
    ...kpiSlice.actions
};

export default kpiSlice.reducer;